import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMyProfile, tryListCompanyUsers } from '../services/usersService';
import { formatDate } from '../lib/format';
import { friendlyError, isPermissionError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';
import DataTable from '../components/ui/DataTable';

export default function UsersPage() {
  const { ruc, user: sessionUser, firebaseUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [colegas, setColegas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [listBlocked, setListBlocked] = useState(false);

  const load = useCallback(async () => {
    if (!ruc) return;
    setLoading(true);
    setError(null);
    try {
      const me = await getMyProfile();
      setProfile(me || sessionUser);
      try {
        const others = await tryListCompanyUsers({ ruc, pageSize: 10 });
        setColegas(others);
        setListBlocked(false);
      } catch (e) {
        if (isPermissionError(e)) {
          setListBlocked(true);
          setColegas([]);
        } else {
          console.warn('[users] list', e?.message);
          setListBlocked(true);
        }
      }
    } catch (e) {
      setError(friendlyError(e));
      setProfile(sessionUser);
    } finally {
      setLoading(false);
    }
  }, [ruc, sessionUser]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando usuarios…" />;

  const columns = [
    { key: 'name', label: 'Nombre', render: (u) => [u.name, u.apellidos].filter(Boolean).join(' ') || '—' },
    { key: 'email', label: 'Correo', render: (u) => u.email || '—' },
    { key: 'rol', label: 'Rol', render: (u) => <Badge tone="info">{u.rol || '—'}</Badge> },
    {
      key: 'activo',
      label: 'Estado',
      render: (u) =>
        u.activo === false ? <Badge tone="muted">Inactivo</Badge> : <Badge tone="ok">Activo</Badge>,
    },
    { key: 'dni', label: 'DNI', render: (u) => u.dni || '—' },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Usuarios</h1>
          <p className="muted">
            Seguridad real en Firebase Auth + Rules. Este panel no crea permisos nuevos.
          </p>
        </div>
      </header>

      {error ? <ErrorState message={error} onRetry={load} /> : null}

      <section className="panel">
        <header className="panel-header">
          <h2>Tu perfil</h2>
          <span className="muted">via getMyUserProfile</span>
        </header>
        {profile ? (
          <div className="meta-grid">
            <div>
              <span className="muted">Nombre</span>
              <div>{[profile.name, profile.apellidos].filter(Boolean).join(' ') || '—'}</div>
            </div>
            <div>
              <span className="muted">Correo</span>
              <div>{profile.email || firebaseUser?.email || '—'}</div>
            </div>
            <div>
              <span className="muted">Rol</span>
              <div>
                <Badge tone="info">{profile.rol || '—'}</Badge>
              </div>
            </div>
            <div>
              <span className="muted">DNI</span>
              <div>{profile.dni || '—'}</div>
            </div>
            <div>
              <span className="muted">Teléfono</span>
              <div>{profile.phone || '—'}</div>
            </div>
            <div>
              <span className="muted">Plan</span>
              <div>{profile.plan || profile.subscriptionStatus || '—'}</div>
            </div>
            <div>
              <span className="muted">RUC empresa</span>
              <div>{profile.ruc || ruc || '—'}</div>
            </div>
            <div>
              <span className="muted">Estado</span>
              <div>
                {profile.activo === false ? (
                  <Badge tone="muted">Inactivo</Badge>
                ) : (
                  <Badge tone="ok">Activo</Badge>
                )}
              </div>
            </div>
          </div>
        ) : (
          <EmptyState title="No se pudo cargar el perfil" />
        )}
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Usuarios de la empresa</h2>
          <span className="muted">máx. 10</span>
        </header>
        {listBlocked ? (
          <div className="alert error" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
            Las reglas de Firestore actuales bloquean el listado de <code>users</code> (solo lectura
            del propio documento o callable <code>getMyUserProfile</code>). Para administrar
            usuarios de la empresa hace falta una Cloud Function server-side (p. ej.{' '}
            <code>listCompanyUsers</code>). El admin no la crea ni bypasea Rules.
          </div>
        ) : colegas.length === 0 ? (
          <EmptyState title="Sin otros usuarios visibles" />
        ) : (
          <DataTable columns={columns} rows={colegas} keyField="id" empty={<EmptyState />} />
        )}
      </section>
    </div>
  );
}
